import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact.php`, formData);
      
      if (response.data.success) {
        toast({
          title: "¡Mensaje Enviado!",
          description: response.data.message,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      
      // Mostrar error específico de validación
      let errorMessage = "Hubo un problema al enviar el mensaje. Por favor inténtalo de nuevo.";
      
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.detail;
        if (Array.isArray(validationErrors)) {
          // Extraer mensajes de error de validación
          errorMessage = validationErrors.map(err => {
            const field = err.loc?.[1] || 'campo';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (validationErrors) {
          errorMessage = validationErrors;
        } else {
          errorMessage = "Por favor verifica que todos los campos estén correctos: nombre (mínimo 2 caracteres), email válido, mensaje (mínimo 10 caracteres).";
        }
      }
      
      toast({
        title: "Error de Validación",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: 'contacto@solutionit.cl',
      link: 'mailto:contacto@solutionit.cl'
    },
    {
      icon: Phone,
      title: 'Teléfono',
      content: '+56 9 6618 6986',
      link: 'tel:+56966186986'
    },
    {
      icon: MapPin,
      title: 'Ubicación',
      content: 'Viña del Mar, Chile',
      link: null
    }
  ];

  return (
    <section id="contact" className="py-20 md:py-32 bg-gray-100">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Contáctanos <span className="text-red-600">Hoy</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Estamos listos para ayudarte a transformar tu infraestructura tecnológica
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Información de Contacto
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              ¿Tienes un proyecto en mente? ¿Necesitas soporte técnico o asesoría? 
              Contáctanos y te responderemos a la brevedad posible.
            </p>

            <div className="space-y-6">
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="text-red-600" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">{info.title}</div>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-gray-600 hover:text-red-600 transition-colors duration-200"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <div className="text-gray-600">{info.content}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Additional Info Box */}
            <div className="mt-8 bg-white p-6 rounded-xl border-2 border-red-100">
              <h4 className="font-bold text-gray-900 mb-2">Horario de Atención</h4>
              <p className="text-gray-600 text-sm">
                Lunes a Viernes: 8:00 AM - 6:00 PM<br />
                Sábados: 9:00 AM - 1:00 PM
              </p>
              <p className="text-red-600 font-medium text-sm mt-2">
                Soporte técnico disponible 24/7
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Solicitar Consulta Gratuita
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Nombre Completo *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Juan Pérez"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="juan@empresa.com"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+56 9 6618 6986"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="company">Empresa</Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Nombre de tu empresa"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="message">Mensaje *</Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Cuéntanos sobre tu proyecto o necesidad tecnológica..."
                  rows={4}
                  className="mt-2"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg transition-colors duration-200"
              >
                {isSubmitting ? 'Enviando...' : (
                  <>
                    Enviar Mensaje
                    <Send className="ml-2" size={20} />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
